import { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faWallet,
  faCircleInfo,
  faExternalLinkAlt,
  faSparkles,
} from '@fortawesome/pro-regular-svg-icons'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import {
  EllipsisText,
  useLabraChat,
  GradientFaIcon,
  LoadingStateComponent,
} from '@labrav/common'
import { FundingWalletHistoryEvent, FundingWalletProgram } from '../../types'
import {
  FundingWalletHistoryUpdate,
  getFundingWalletHistory,
} from '../../api/fundingApi'
import { formatAmount, formatWalletHistoryTime } from '../../utils/format'
import { WalletBar, currencySymbol } from './WalletBar'
import { WalletHelpTopic, WalletHistoryModal } from './WalletHistoryModal'
import {
  WALLET_OVERVIEW_PROMPT,
  walletHelpPrompt,
  walletShortfallPrompt,
} from './walletHelpPrompts'
import { ScrollHint } from '../ScrollHint/ScrollHint'
import { useStyles } from './FundingWallet.styles'

interface FundingWalletProps {
  programs?: FundingWalletProgram[]
  loading?: boolean
  serviceUrl?: string
  partnerId?: string
}

const WALLET_INFO_TEXT =
  'Wallets are allocated cash or credit provided and controlled by AWS ' +
  'team, from these wallets allocation the value is deducted once the funding ' +
  'is submitted for some of the funding programs. '

const HOVER_ACTION = 'walletHoverAction'
const REVIEW_BTN = 'walletReviewBtn'
const WARN_TEXT = 'walletWarnText'

const toDelta = (changes: FundingWalletHistoryUpdate['changes']) => {
  for (const change of changes ?? []) {
    if (change.previous_value == null || change.current_value == null) continue
    const prev = Number(change.previous_value)
    const cur = Number(change.current_value)
    if (Number.isNaN(prev) || Number.isNaN(cur)) continue
    const diff = cur - prev
    if (diff === 0) continue
    return {
      sign: diff > 0 ? ('+' as const) : ('-' as const),
      amount: Math.abs(diff),
    }
  }
  return null
}

const toHistoryEvent = (
  update: FundingWalletHistoryUpdate
): FundingWalletHistoryEvent => ({
  id: update.id,
  eventType: update.event_type,
  summary: update.summary,
  delta: toDelta(update.changes),
  timestamp: formatWalletHistoryTime(update.event_at),
})

interface ProgramCardProps {
  program: FundingWalletProgram
  onViewHistory: () => void
  onReviewAI: () => void
}

const ProgramCard = ({
  program,
  onViewHistory,
  onReviewAI,
}: ProgramCardProps) => {
  const { classes, cx } = useStyles()

  return (
    <div
      className={classes.program}
      data-testid={`wallet-program-${program.id}`}
    >
      <div className={classes.programHeader}>
        <EllipsisText text={program.name} className={classes.programName} />
        <div className={classes.headerActions}>
          {program.lastUpdated && (
            <Typography className={classes.lastUpdated}>
              Last updated on {program.lastUpdated}
            </Typography>
          )}
          <button
            type="button"
            className={cx(classes.viewHistoryBtn, HOVER_ACTION)}
            onClick={onViewHistory}
            data-testid={`wallet-view-history-${program.id}`}
          >
            View history
          </button>
        </div>
      </div>

      <WalletBar program={program} />

      {program.shortfall != null && program.shortfall > 0 && (
        <div className={classes.warning}>
          <Typography className={cx(classes.warningText, WARN_TEXT)}>
            Your funding requests exceed your wallet balance by{' '}
            <b>
              {currencySymbol(program.currency)}
              {formatAmount(program.shortfall)}.
            </b>{' '}
            Contact your <b>{program.contactLabel ?? 'AWS PDM'}</b> for
            additional funding.
          </Typography>
          <button
            type="button"
            className={cx(classes.reviewBtn, REVIEW_BTN)}
            onClick={onReviewAI}
            data-testid={`wallet-review-ai-${program.id}`}
          >
            <GradientFaIcon icon={faSparkles} className={classes.reviewIcon} />
            <Typography component="span" className={classes.reviewText}>
              Review with AI
            </Typography>
          </button>
        </div>
      )}
    </div>
  )
}

export const FundingWallet = ({
  programs = [],
  loading = false,
  serviceUrl,
  partnerId,
}: FundingWalletProps) => {
  const { classes } = useStyles()
  const { open, startNewChat, sendPrompt } = useLabraChat()
  const programsRef = useRef<HTMLDivElement>(null)
  const [historyProgram, setHistoryProgram] =
    useState<FundingWalletProgram | null>(null)
  const [events, setEvents] = useState<FundingWalletHistoryEvent[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  useEffect(() => {
    if (!historyProgram || !serviceUrl || !partnerId) return
    let ignore = false
    setHistoryLoading(true)
    setEvents([])
    ;(async () => {
      try {
        const { data } = await getFundingWalletHistory(
          serviceUrl,
          partnerId,
          historyProgram.id
        )
        if (!ignore) setEvents((data.updates ?? []).map(toHistoryEvent))
      } catch {
        if (!ignore) setEvents([])
      } finally {
        if (!ignore) setHistoryLoading(false)
      }
    })()
    return () => {
      ignore = true
    }
  }, [historyProgram, serviceUrl, partnerId])

  const reviewWithAI = (program: FundingWalletProgram) => {
    open()
    startNewChat()
    sendPrompt(`Review my ${program.name}`)
  }

  const askAgent = (prompt: string) => {
    open()
    startNewChat()
    sendPrompt(prompt, { showUserMessage: true })
  }

  const onHistoryHelp = (topic: WalletHelpTopic) => {
    if (!historyProgram) return
    const name = historyProgram.name
    askAgent(
      topic === 'shortfall'
        ? walletShortfallPrompt(
            name,
            `${currencySymbol(historyProgram.currency)}${formatAmount(
              historyProgram.shortfall ?? 0
            )}`
          )
        : walletHelpPrompt(name)
    )
  }

  return (
    <section className={classes.card} data-testid="funding-wallet">
      <div className={classes.header}>
        <span className={classes.headerIcon}>
          <FontAwesomeIcon icon={faWallet} />
        </span>
        <Typography component="h2" className={classes.title}>
          Funding wallet
        </Typography>
        <Tooltip
          title={
            <span>
              {WALLET_INFO_TEXT}
              <button
                type="button"
                className={classes.learnMore}
                data-testid="wallet-learn-more"
                onClick={() => askAgent(WALLET_OVERVIEW_PROMPT)}
              >
                Learn more
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </button>
            </span>
          }
          arrow
          placement="top"
        >
          <span>
            <FontAwesomeIcon icon={faCircleInfo} className={classes.infoIcon} />
          </span>
        </Tooltip>
      </div>
      {loading ? (
        <div className={classes.loading}>
          <LoadingStateComponent
            heading="Fetching your wallet"
            description="It may take a few moments, please wait..."
          />
        </div>
      ) : (
        <div className={classes.scrollWrap}>
          <div ref={programsRef} className={classes.programs}>
            {programs.map(p => (
              <ProgramCard
                key={p.id}
                program={p}
                onViewHistory={() => setHistoryProgram(p)}
                onReviewAI={() => reviewWithAI(p)}
              />
            ))}
          </div>
          <ScrollHint scrollRef={programsRef} contentKey={programs.length} />
        </div>
      )}

      <WalletHistoryModal
        open={historyProgram != null}
        program={historyProgram}
        events={events}
        loading={historyLoading}
        onClose={() => setHistoryProgram(null)}
        onHelp={onHistoryHelp}
      />
    </section>
  )
}

export default FundingWallet
